import { useState } from "react";
import { Link } from "react-router";
import type { Customer, CustomerSortBy } from "../services/customersApi";
import type { CustomerGroup } from "@/features/customerGroups/services/customerGroupsApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  History,
  MoreHorizontal,
  Search,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteCustomer } from "../hooks/useCustomer";
import { EditCustomerDialog } from "./EditCustomerDialog";
import { CustomerDiscountDialog } from "./CustomerDiscountsDialog";
import { SowingThresholdDialog } from "./SowingThresholdDialog";
import { CustomerOrderHistoryDialog } from "./CustomerOrderHistoryDialog";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import type { County } from "@/shared/services/metadataApi";

interface SortState {
  by: CustomerSortBy;
  direction: "asc" | "desc";
}

interface CustomersTableProps {
  customers: Customer[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  sort: SortState;
  onSortChange: (column: CustomerSortBy) => void;
  customerGroups: CustomerGroup[];
  selectedCustomerGroupId: string | undefined;
  onCustomerGroupChange: (groupId: string | undefined) => void;
  counties: County[];
  selectedCounty: string | undefined;
  onCountyChange: (county: string | undefined) => void;
}

interface SortableHeaderProps {
  column: CustomerSortBy;
  label: string;
  sort: SortState;
  onSortChange: (column: CustomerSortBy) => void;
  className?: string;
}

const SortableHeader = ({
  column,
  label,
  sort,
  onSortChange,
  className,
}: SortableHeaderProps) => {
  const active = sort.by === column;
  const Icon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        className="-ml-2 inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-accent hover:text-accent-foreground"
        onClick={() => onSortChange(column)}
      >
        {label}
        <Icon className="h-3.5 w-3.5 opacity-70" />
      </button>
    </TableHead>
  );
};

export const CustomersTable = ({
  customers,
  searchValue,
  onSearchChange,
  sort,
  onSortChange,
  customerGroups,
  selectedCustomerGroupId,
  onCustomerGroupChange,
  counties,
  selectedCounty,
  onCountyChange,
}: CustomersTableProps) => {
  const deleteMutation = useDeleteCustomer();
  const confirm = useConfirm();
  // Sowing-threshold override is a GM-only financial control — gate behind a dedicated permission
  // (backend enforces customers.set_sowing_threshold on PATCH; hide the menu item to match).
  const canSetSowingThreshold = useAuthStore((s) =>
    s.hasPermission("customers.set_sowing_threshold")
  );

  const [historyCustomer, setHistoryCustomer] = useState<Customer | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const openHistory = (customer: Customer) => {
    setHistoryCustomer(customer);
    setHistoryOpen(true);
  };

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: "Delete customer?",
      description: `“${name}” will be permanently removed. This can't be undone.`,
      variant: "destructive",
      confirmLabel: "Delete",
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(id), `${name} deleted`),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 justify-end">
        <Select
          value={selectedCustomerGroupId ?? "all"}
          onValueChange={(value) =>
            onCustomerGroupChange(value === "all" ? undefined : value)
          }
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="All Customer Groups" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Customer Groups</SelectItem>
            {customerGroups.map((group) => (
              <SelectItem key={group.id} value={group.id}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={selectedCounty ?? "all"}
          onValueChange={(value) =>
            onCountyChange(value === "all" ? undefined : value)
          }
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="All Regions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            {counties.map((county) => (
              <SelectItem key={county.code} value={county.name}>
                {county.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search by name or mobile..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader
                column="customerName"
                label="Customer Name"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHeader
                column="email"
                label="Email"
                sort={sort}
                onSortChange={onSortChange}
              />
              <TableHead>Mobile</TableHead>
              <SortableHeader
                column="town"
                label="Location"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHeader
                column="creditLimit"
                label="Credit Limit"
                sort={sort}
                onSortChange={onSortChange}
              />
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.map((customer) => (
              <TableRow
                key={customer.id}
                className="cursor-pointer hover:bg-muted/50"
                role="button"
                tabIndex={0}
                onClick={() => openHistory(customer)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openHistory(customer);
                  }
                }}
              >
                <TableCell className="font-medium">
                  <Link
                    to={`/customers/${customer.id}`}
                    className="hover:underline text-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {customer.customerName}
                  </Link>
                </TableCell>
                <TableCell>
                  {customer.email ?? (
                    <span className="text-muted-foreground italic">No email</span>
                  )}
                </TableCell>
                <TableCell>{customer.mobileNo1}</TableCell>
                <TableCell>
                  {customer.town}, {customer.county}
                </TableCell>
                <TableCell>{(customer.creditLimit ?? 0).toLocaleString()}</TableCell>
                <TableCell>
                  <Badge
                    variant={customer.isActive ? "default" : "destructive"}
                  >
                    {customer.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell
                  className="text-right"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => openHistory(customer)}>
                        <History className="w-4 h-4 mr-2" />
                        View Order History
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <EditCustomerDialog customer={customer} />
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <CustomerDiscountDialog customer={customer} />
                      </DropdownMenuItem>
                      {canSetSowingThreshold && (
                        <DropdownMenuItem asChild>
                          <SowingThresholdDialog customer={customer} />
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() =>
                          handleDelete(customer.id, customer.customerName)
                        }
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {customers.length === 0 && (
        <div className="text-center py-8 text-gray-500">No customers found</div>
      )}

      <CustomerOrderHistoryDialog
        customer={historyCustomer}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </div>
  );
};
